import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const razorpayOrder = params.get("razorpay_order");
  const [tx, setTx] = useState(null);
  const [failed, setFailed] = useState(false);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId && !razorpayOrder) { setFailed(true); return; }
    const endpoint = razorpayOrder ? `/razorpay/status/${razorpayOrder}` : `/payments/status/${sessionId}`;
    const poll = async () => {
      try {
        const { data } = await api.get(endpoint);
        setTx(data);
        if (data.payment_status !== "paid" && tries.current < 15) {
          tries.current += 1;
          setTimeout(poll, 2000);
        } else if (data.payment_status !== "paid") {
          setFailed(true);
        }
      } catch (e) {
        setFailed(true);
      }
    };
    poll();
  }, [sessionId, razorpayOrder]);

  return (
    <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="payment-success-page">
      {!tx && !failed && (
        <div>
          <Loader2 size={40} className="text-gold mx-auto mb-5 animate-spin" />
          <h1 className="font-display text-2xl font-bold text-slate-100">CONFIRMING YOUR PAYMENT…</h1>
          <p className="text-sm text-slate-500 mt-3">Securely verifying the transaction with Stripe.</p>
        </div>
      )}
      {tx?.payment_status === "paid" && (
        <div data-testid="payment-status-paid">
          <CheckCircle2 size={48} className="text-gold mx-auto mb-5" />
          <p className="gold-kicker mb-3">Payment Confirmed</p>
          <h1 className="font-display text-3xl font-bold text-slate-100">WELCOME TO THE NETWORK.</h1>
          <div className="card-surface hairline-gold p-6 mt-8 text-left">
            <div className="flex justify-between text-sm border-b border-slate-800 pb-3">
              <span className="text-slate-500">Plan</span><span className="text-slate-100 font-semibold">{tx.plan}</span>
            </div>
            <div className="flex justify-between text-sm pt-3">
              <span className="text-slate-500">Amount</span>
              <span className="font-mono text-gold">{tx.currency?.toUpperCase()} {Number(tx.amount).toLocaleString("en-IN")}</span>
            </div>
          </div>
          <p className="text-sm text-slate-400 mt-6">Your membership is active. Complete your business profile to unlock verification, AI matching and opportunity intelligence.</p>
          <div className="flex justify-center gap-3 mt-8">
            <Link to="/join" data-testid="payment-complete-profile-btn" className="btn-gold text-xs">COMPLETE YOUR PROFILE</Link>
            <Link to="/" className="btn-outline text-xs">BACK TO HOME</Link>
          </div>
        </div>
      )}
      {(failed || (tx && tx.payment_status !== "paid" && tries.current >= 15)) && (
        <div data-testid="payment-status-failed">
          <XCircle size={48} className="text-red-400 mx-auto mb-5" />
          <h1 className="font-display text-2xl font-bold text-slate-100">PAYMENT NOT CONFIRMED</h1>
          <p className="text-sm text-slate-400 mt-3">We couldn't confirm this transaction. If the amount was debited, it will reconcile automatically — or contact the network desk.</p>
          <div className="flex justify-center gap-3 mt-8">
            <Link to="/membership" className="btn-gold text-xs" data-testid="payment-retry-btn">BACK TO MEMBERSHIP</Link>
            <Link to="/contact" className="btn-outline text-xs">CONTACT US</Link>
          </div>
        </div>
      )}
    </div>
  );
}
